import { useCallback, useEffect, useRef, useState } from 'react';
import { getPlaceNameFromCoordinates } from '../utils/mapbox';

export const GEOLOCATION_TIMEOUT = 10000;

/** @returns {Promise<number[]>} [longitude, latitude] */
const getDeviceCoordinates = () =>
  new Promise((resolve, reject) => {
    if (!navigator?.geolocation?.getCurrentPosition) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve([coords.longitude, coords.latitude]),
      reject,
      { timeout: GEOLOCATION_TIMEOUT },
    );
  });

/**
 * Finds the user's location and reverse-geocodes it once per `locate()` call.
 *
 * `locate()` resolves to `{ coordinates, placeName }`, or to `null` if anything
 * fails (unsupported, permission denied, timeout, or Mapbox). It never rejects.
 * Results aren't stored, so nothing is reused after a remount.
 *
 * `coordinates` are in Mapbox `[longitude, latitude]` order. `placeName` is never
 * empty: a location Mapbox can't name (e.g. outside the supported countries)
 * counts as a failure, since keyword search couldn't find it either.
 */
const useGeoLocation = () => {
  const [inProgress, setInProgress] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /** @returns {Promise<{ coordinates: number[], placeName: string } | null>} */
  const locate = useCallback(async () => {
    setInProgress(true);

    try {
      const coordinates = await getDeviceCoordinates();
      const placeName = await getPlaceNameFromCoordinates(coordinates);
      if (!placeName) throw new Error('No place found at this location');

      if (isMounted.current) setInProgress(false);
      return { coordinates, placeName };
    } catch {
      if (isMounted.current) setInProgress(false);
      return null;
    }
  }, []);

  return { locate, inProgress };
};

export default useGeoLocation;
